import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useCurrency } from '../context/CurrencyContext'

export const INVESTMENT_TYPES = [
  { value: 'fd',          label: 'Fixed Deposit', icon: '🏛', color: '#3d4a5c' },
  { value: 'ppf',         label: 'PPF',           icon: '📮', color: '#4a6741' },
  { value: 'mutual_fund', label: 'Mutual Fund',   icon: '📊', color: '#6b4c9a' },
  { value: 'sip',         label: 'SIP',           icon: '🔄', color: '#7a5d1e' },
  { value: 'stocks',      label: 'Stocks',        icon: '📈', color: '#b8441a' },
  { value: 'etf',         label: 'ETF',           icon: '⚡', color: '#c9a84c' },
  { value: 'gold',        label: 'Gold',          icon: '🥇', color: '#c07c2a' },
  { value: 'real_estate', label: 'Real Estate',   icon: '🏠', color: '#5a7a3a' },
  { value: 'other',       label: 'Other',         icon: '📦', color: '#636e72' },
]

const SIP_FREQS = [
  { value: 'monthly',   label: 'Monthly'   },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly',    label: 'Yearly'    },
]

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function fmtDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  return MONTHS[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear()
}

function fmtTime(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
}

function getType(value) {
  return INVESTMENT_TYPES.find(t => t.value === value) || INVESTMENT_TYPES[INVESTMENT_TYPES.length - 1]
}

// A = P(1 + r/n)^(n*t) — n=4 for FD (quarterly), n=1 for PPF (yearly)
function calcCompoundValue(principal, ratePercent, fromDateStr, toDateStr, n) {
  const P = parseFloat(principal)
  const r = parseFloat(ratePercent) / 100
  if (!P || !r || !fromDateStr || !toDateStr) return null
  const years = (new Date(toDateStr) - new Date(fromDateStr)) / (365.25 * 24 * 60 * 60 * 1000)
  if (years <= 0) return P
  return P * Math.pow(1 + r / n, n * years)
}

// mfapi.in — CORS-friendly public API
async function fetchMFNav(schemeCode) {
  const res = await fetch(`https://api.mfapi.in/mf/${schemeCode}`)
  if (!res.ok) throw new Error('HTTP ' + res.status)
  const json = await res.json()
  const latest = json.data?.[0]
  if (!latest) throw new Error('No NAV data returned')
  return {
    price:      parseFloat(latest.nav),
    label:      'NAV',
    date:       latest.date,
    schemeName: json.meta?.scheme_name || '',
  }
}

// Yahoo Finance v8 chart — works for NSE (.NS), BSE (.BO), US tickers
async function fetchStockPrice(ticker) {
  const url = `https://query1.finance.yahoo.com/v8/finance/chart/${encodeURIComponent(ticker)}?range=1d&interval=1d&includePrePost=false`
  const res = await fetch(url)
  if (!res.ok) throw new Error('HTTP ' + res.status)
  const json = await res.json()
  const result = json?.chart?.result?.[0]
  if (!result) throw new Error('No data in response')
  return {
    price:        result.meta.regularMarketPrice,
    label:        'Price',
    currency:     result.meta.currency,
    exchangeName: result.meta.exchangeName || ticker,
  }
}

// Compute effective current value using live data if available
function effectiveValue(inv, liveMap) {
  const live = liveMap[inv.id]
  if (!live || live.error) return parseFloat(inv.current_value || 0)
  const qty = parseFloat(inv.quantity || 0)
  // FD/PPF: live.price is already the total accrued value
  if (['fd', 'ppf'].includes(inv.type)) return live.price
  // MF/SIP/Stocks/ETF: price per unit × quantity
  if (qty > 0) return live.price * qty
  return live.price || parseFloat(inv.current_value || 0)
}

const inputStyle = {
  background: 'var(--input-bg)', border: '1px solid var(--border)',
  borderRadius: '4px', padding: '0.55rem 0.75rem',
  fontSize: '0.88rem', fontFamily: 'DM Sans, sans-serif',
  color: 'var(--text)', width: '100%', boxSizing: 'border-box',
  outline: 'none', transition: 'border-color 0.18s',
}

const labelStyle = {
  display: 'block', fontFamily: 'monospace', fontSize: '0.67rem',
  fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.08em',
  color: 'var(--text-muted)', marginBottom: '0.35rem',
}

// ─── Add / Edit Modal ─────────────────────────────────────────────────────────

const EMPTY = {
  type: 'mutual_fund', name: '',
  invested_amount: '', current_value: '',
  quantity: '', purchase_price: '',
  interest_rate: '',
  sip_amount: '', sip_frequency: 'monthly',
  purchase_date: '', maturity_date: '',
  scheme_code: '', ticker_symbol: '',
  notes: '',
}

function AddInvestmentModal({ investment, onSave, onClose }) {

  const [form, setForm]   = useState(EMPTY)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  // Populate form when editing
  useEffect(() => {
    if (investment) {
      setForm({
        type:            investment.type            || 'mutual_fund',
        name:            investment.name            || '',
        invested_amount: investment.invested_amount != null ? String(investment.invested_amount) : '',
        current_value:   investment.current_value   != null ? String(investment.current_value)  : '',
        quantity:        investment.quantity        != null ? String(investment.quantity)        : '',
        purchase_price:  investment.purchase_price  != null ? String(investment.purchase_price) : '',
        interest_rate:   investment.interest_rate   != null ? String(investment.interest_rate)  : '',
        sip_amount:      investment.sip_amount      != null ? String(investment.sip_amount)     : '',
        sip_frequency:   investment.sip_frequency   || 'monthly',
        purchase_date:   investment.purchase_date   || '',
        maturity_date:   investment.maturity_date   || '',
        scheme_code:     investment.scheme_code     || '',
        ticker_symbol:   investment.ticker_symbol   || '',
        notes:           investment.notes           || '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [investment])

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  // Type flags
  const isStockLike  = ['stocks', 'etf', 'gold'].includes(form.type)
  const isFDLike     = ['fd', 'ppf'].includes(form.type)
  const isSIP        = form.type === 'sip'
  const isMF         = ['mutual_fund', 'sip'].includes(form.type)
  const hasTicker    = ['stocks', 'etf'].includes(form.type)
  const hasMFUnits   = ['mutual_fund', 'sip'].includes(form.type)

  // Auto-calc: invested_amount = quantity × purchase_price (stocks/ETF/gold)
  useEffect(() => {
    if (!['stocks', 'etf', 'gold'].includes(form.type)) return
    const qty   = parseFloat(form.quantity)
    const price = parseFloat(form.purchase_price)
    if (qty > 0 && price > 0) {
      setForm(prev => ({ ...prev, invested_amount: (qty * price).toFixed(2) }))
    }
  }, [form.quantity, form.purchase_price, form.type])

  // Auto-calc: current_value for FD/PPF via compound interest up to today
  useEffect(() => {
    if (!['fd', 'ppf'].includes(form.type)) return
    if (!form.invested_amount || !form.interest_rate || !form.purchase_date) return
    const n = form.type === 'ppf' ? 1 : 4
    const today = new Date().toISOString().slice(0, 10)
    const val = calcCompoundValue(form.invested_amount, form.interest_rate, form.purchase_date, today, n)
    if (val !== null) {
      setForm(prev => ({ ...prev, current_value: val.toFixed(2) }))
    }
  }, [form.invested_amount, form.interest_rate, form.purchase_date, form.type])

  // Derived maturity preview for FD/PPF display
  const maturityPreview = isFDLike && form.invested_amount && form.interest_rate && form.purchase_date && form.maturity_date
    ? calcCompoundValue(form.invested_amount, form.interest_rate, form.purchase_date, form.maturity_date, form.type === 'ppf' ? 1 : 4)
    : null

  async function handleSave() {
    if (!form.name.trim()) { setError('Please enter an investment name'); return }
    const invAmt = parseFloat(form.invested_amount)
    if (!form.invested_amount || isNaN(invAmt) || invAmt < 0) {
      setError('Please enter a valid invested amount'); return
    }
    // current_value defaults to invested_amount if blank
    const curVal = form.current_value !== '' ? parseFloat(form.current_value) : invAmt

    setSaving(true)
    setError('')
    try {
      await onSave({
        ...(investment?.id ? { id: investment.id } : {}),
        type:            form.type,
        name:            form.name.trim(),
        invested_amount: invAmt,
        current_value:   curVal,
        quantity:        form.quantity       ? parseFloat(form.quantity)              : null,
        purchase_price:  form.purchase_price ? parseFloat(form.purchase_price)        : null,
        interest_rate:   form.interest_rate  ? parseFloat(form.interest_rate)         : null,
        sip_amount:      form.sip_amount     ? parseFloat(form.sip_amount)            : null,
        sip_frequency:   isSIP               ? form.sip_frequency                     : null,
        purchase_date:   form.purchase_date  || null,
        maturity_date:   form.maturity_date  || null,
        scheme_code:     form.scheme_code.trim()                                      || null,
        ticker_symbol:   form.ticker_symbol.trim().toUpperCase()                      || null,
        notes:           form.notes.trim()                                            || null,
      })
    } catch (e) {
      setError('Save failed: ' + e.message)
      setSaving(false)
    }
    // modal unmounts on success; setSaving(false) only reached on error path above
  }

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
    >
      <div style={{ background: 'var(--surface)', borderRadius: '10px', border: '1px solid var(--border)', width: '100%', maxWidth: '520px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)' }}>
          <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.15rem', fontWeight: '700' }}>
            {investment ? 'Edit Investment' : 'Add Investment'}
          </span>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 1, padding: '0.2rem' }}
          >✕</button>
        </div>

        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          {error && (
            <div style={{ background: 'rgba(184,68,26,0.08)', border: '1px solid rgba(184,68,26,0.2)', borderRadius: '6px', padding: '0.7rem 1rem', fontSize: '0.82rem', color: '#b8441a' }}>
              ⚠️ {error}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>

            {/* Type */}
            <div>
              <label style={labelStyle}>Type</label>
              <select style={inputStyle} value={form.type} onChange={e => set('type', e.target.value)}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--text)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                {INVESTMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
              </select>
            </div>

            {/* Purchase date */}
            <div>
              <label style={labelStyle}>Purchase Date</label>
              <input type="date" style={inputStyle} value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--text)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'} />
            </div>

            {/* Name */}
            <div style={{ gridColumn: '1/-1' }}>
              <label style={labelStyle}>Investment Name</label>
              <input style={inputStyle} placeholder="e.g. HDFC Flexi Cap Fund, Reliance Industries, SBI FD"
                value={form.name} onChange={e => set('name', e.target.value)}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--text)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'} />
            </div>

            {/* Ticker symbol (stocks/ETF) */}
            {hasTicker && (
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Ticker Symbol <span style={{ textTransform: 'none', letterSpacing: 0, fontSize: '0.62rem', color: 'var(--text-muted)' }}>(e.g. RELIANCE.NS, AAPL)</span></label>
                <input style={inputStyle} placeholder="e.g. RELIANCE.NS for NSE, AAPL for US"
                  value={form.ticker_symbol} onChange={e => set('ticker_symbol', e.target.value.toUpperCase())}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--text)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'} />
              </div>
            )}

            {/* Scheme code (mutual fund / SIP) */}
            {isMF && (
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>MF Scheme Code <span style={{ textTransform: 'none', letterSpacing: 0, fontSize: '0.62rem', color: 'var(--text-muted)' }}>(from mfapi.in, for live NAV)</span></label>
                <input style={inputStyle} placeholder="e.g. 120503 for Axis Long Term Equity Fund"
                  value={form.scheme_code} onChange={e => set('scheme_code', e.target.value)}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--text)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'} />
              </div>
            )}

            {/* Quantity (stocks/ETF/gold) or Units (MF/SIP) */}
            {(isStockLike || hasMFUnits) && (
              <div>
                <label style={labelStyle}>{hasMFUnits ? 'Units Held' : 'Quantity'}</label>
                <input type="text" inputMode="decimal" style={inputStyle}
                  placeholder={hasMFUnits ? 'e.g. 150.234' : 'e.g. 50'}
                  value={form.quantity} onChange={e => set('quantity', e.target.value)}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--text)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'} />
              </div>
            )}

            {/* Purchase price (stocks/ETF/gold only — auto-fills invested_amount) */}
            {isStockLike && (
              <div>
                <label style={labelStyle}>Purchase Price / Unit (₹)</label>
                <input type="text" inputMode="decimal" style={inputStyle} placeholder="e.g. 1500"
                  value={form.purchase_price} onChange={e => set('purchase_price', e.target.value)}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--text)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'} />
              </div>
            )}

            {/* Total invested */}
            <div style={{ gridColumn: isStockLike ? '1/-1' : (hasMFUnits ? '2/3' : '1/-1') }}>
              <label style={labelStyle}>
                Total Invested (₹)
                {isStockLike && form.quantity && form.purchase_price && (
                  <span style={{ fontWeight: '400', textTransform: 'none', letterSpacing: 0, marginLeft: '0.4rem', color: '#c9a84c' }}>auto-calculated</span>
                )}
              </label>
              <input type="text" inputMode="decimal" style={inputStyle} placeholder="e.g. 100000"
                value={form.invested_amount} onChange={e => set('invested_amount', e.target.value)}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--text)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'} />
            </div>

            {/* Interest rate (FD/PPF) */}
            {isFDLike && (
              <div>
                <label style={labelStyle}>Interest Rate (% p.a.)</label>
                <input type="text" inputMode="decimal" style={inputStyle} placeholder="e.g. 7.10"
                  value={form.interest_rate} onChange={e => set('interest_rate', e.target.value)}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--text)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'} />
              </div>
            )}

            {/* Maturity date (FD/PPF) */}
            {isFDLike && (
              <div>
                <label style={labelStyle}>Maturity Date</label>
                <input type="date" style={inputStyle} value={form.maturity_date} onChange={e => set('maturity_date', e.target.value)}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--text)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'} />
              </div>
            )}

            {/* Current value — auto-filled for FD/PPF, manual otherwise */}
            <div style={{ gridColumn: '1/-1' }}>
              <label style={labelStyle}>
                Current Value (₹)
                {isFDLike && form.interest_rate && (
                  <span style={{ fontWeight: '400', textTransform: 'none', letterSpacing: 0, marginLeft: '0.4rem', color: '#c9a84c' }}>auto-calculated (accrued to today)</span>
                )}
              </label>
              <input type="text" inputMode="decimal" style={inputStyle} placeholder="Current market / accrued value"
                value={form.current_value} onChange={e => set('current_value', e.target.value)}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--text)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'} />
            </div>

            {/* FD/PPF maturity preview */}
            {maturityPreview !== null && (
              <div style={{ gridColumn: '1/-1', background: 'rgba(74,103,65,0.07)', border: '1px solid rgba(74,103,65,0.2)', borderRadius: '6px', padding: '0.6rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontFamily: 'monospace', fontSize: '0.67rem', color: '#4a6741', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Maturity Value ({form.type === 'ppf' ? 'Yearly' : 'Quarterly'} compounding)
                </span>
                <span style={{ fontFamily: 'Georgia,serif', fontSize: '1rem', fontWeight: '700', color: '#4a6741' }}>
                  ₹{maturityPreview.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}

            {/* SIP fields */}
            {isSIP && (
              <>
                <div>
                  <label style={labelStyle}>SIP Amount (₹)</label>
                  <input type="text" inputMode="decimal" style={inputStyle} placeholder="e.g. 5000"
                    value={form.sip_amount} onChange={e => set('sip_amount', e.target.value)}
                    onFocus={e => e.currentTarget.style.borderColor = 'var(--text)'}
                    onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'} />
                </div>
                <div>
                  <label style={labelStyle}>SIP Frequency</label>
                  <select style={inputStyle} value={form.sip_frequency} onChange={e => set('sip_frequency', e.target.value)}
                    onFocus={e => e.currentTarget.style.borderColor = 'var(--text)'}
                    onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                    {SIP_FREQS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
              </>
            )}

            {/* Notes */}
            <div style={{ gridColumn: '1/-1' }}>
              <label style={labelStyle}>Notes (optional)</label>
              <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: '60px' }}
                placeholder="Any notes..."
                value={form.notes} onChange={e => set('notes', e.target.value)}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--text)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'} />
            </div>

          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '0.9rem 1.5rem 1.3rem', display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', borderTop: '1px solid var(--border)' }}>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', padding: '0.5rem 1rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer' }}
          >Cancel</button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{ background: 'var(--btn-bg)', color: 'var(--btn-text)', border: 'none', padding: '0.5rem 1.2rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: '500', cursor: saving ? 'not-allowed' : 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: saving ? 0.7 : 1 }}
          >{saving ? 'Saving...' : investment ? 'Update' : 'Add Investment'}</button>
        </div>

      </div>
    </div>
  )
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────

function InvestmentDetailModal({ investment, history, liveEntry, fmt, onUpdateValue, onClose }) {
  const [newVal,   setNewVal]   = useState('')
  const [newNotes, setNewNotes] = useState('')
  const [updating, setUpdating] = useState(false)
  const [showForm, setShowForm] = useState(false)

  const t        = getType(investment.type)
  const invested = parseFloat(investment.invested_amount || 0)
  const qty      = parseFloat(investment.quantity || 0)

  // P&L uses live price if available
  let current = parseFloat(investment.current_value || 0)
  let liveLabel = ''
  if (liveEntry && !liveEntry.error) {
    if (['fd', 'ppf'].includes(investment.type)) {
      current   = liveEntry.price
      liveLabel = 'Compound calc'
    } else if (qty > 0) {
      current   = liveEntry.price * qty
      liveLabel = `Live ${liveEntry.label} × ${qty} units`
    } else {
      current   = liveEntry.price
      liveLabel = `Live ${liveEntry.label}`
    }
  }

  const costBasis = (investment.purchase_price != null && qty > 0)
    ? parseFloat(investment.purchase_price) * qty
    : invested
  const pl    = current - costBasis
  const plPct = costBasis > 0 ? (pl / costBasis) * 100 : 0
  const plClr = pl > 0 ? '#4a6741' : pl < 0 ? '#b8441a' : 'var(--text-muted)'
  const plSign = pl >= 0 ? '+' : '−'

  const invHistory = (history || [])
    .filter(h => h.investment_id === investment.id)
    .sort((a, b) => new Date(b.recorded_at || b.created_at) - new Date(a.recorded_at || a.created_at))

  // FD/PPF maturity preview
  const isFDLike = ['fd', 'ppf'].includes(investment.type)
  const maturityVal = isFDLike && investment.invested_amount && investment.interest_rate && investment.purchase_date && investment.maturity_date
    ? calcCompoundValue(investment.invested_amount, investment.interest_rate, investment.purchase_date, investment.maturity_date, investment.type === 'ppf' ? 1 : 4)
    : null

  async function handleUpdate() {
    const v = parseFloat(newVal)
    if (!newVal || isNaN(v) || v < 0) return
    setUpdating(true)
    try { await onUpdateValue(investment.id, v, newNotes.trim() || null) } catch {}
    setNewVal(''); setNewNotes(''); setShowForm(false); setUpdating(false)
  }

  const row = (label, value, vc) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0.6rem 0', borderBottom: '1px solid var(--surface-hdr)' }}>
      <span style={{ fontFamily: 'monospace', fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.9rem', fontWeight: '600', color: vc || 'var(--text)' }}>{value}</span>
    </div>
  )

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: 'var(--surface)', borderRadius: '10px', border: '1px solid var(--border)', width: '100%', maxWidth: '460px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)' }}>
          <div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.1rem', fontWeight: '700', marginBottom: '0.3rem' }}>
              {t.icon} {investment.name}
            </div>
            <span style={{ display: 'inline-block', fontFamily: 'monospace', fontSize: '0.62rem', padding: '0.12rem 0.45rem', borderRadius: '3px', background: t.color + '18', color: t.color, border: '1px solid ' + t.color + '44', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {t.label}
            </span>
            {liveEntry && !liveEntry.error && (
              <span style={{ marginLeft: '0.5rem', display: 'inline-block', fontFamily: 'monospace', fontSize: '0.58rem', padding: '0.1rem 0.4rem', borderRadius: '3px', background: 'rgba(74,103,65,0.1)', color: '#4a6741', border: '1px solid rgba(74,103,65,0.25)' }}>
                ● Live · {fmtTime(liveEntry.updatedAt)}
              </span>
            )}
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 1, padding: '0.2rem', marginLeft: '1rem' }}>✕</button>
        </div>

        {/* P&L hero */}
        <div style={{ padding: '1.25rem 1.5rem', background: pl >= 0 ? 'rgba(74,103,65,0.06)' : 'rgba(184,68,26,0.06)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
              {pl >= 0 ? 'Total Gain' : 'Total Loss'}
            </div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.9rem', fontWeight: '900', color: plClr, lineHeight: 1 }}>
              {plSign}{fmt(Math.abs(pl))}
            </div>
            {liveLabel && <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: '#4a6741', marginTop: '0.25rem' }}>● {liveLabel}</div>}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>Return</div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.9rem', fontWeight: '900', color: plClr, lineHeight: 1 }}>
              {plSign}{Math.abs(plPct).toFixed(2)}%
            </div>
          </div>
        </div>

        {/* Detail rows */}
        <div style={{ padding: '0.4rem 1.5rem 0.9rem' }}>
          {row('Invested Amount', fmt(invested))}
          {row('Current Value', fmt(current), plClr)}
          {liveEntry && !liveEntry.error && row('Live ' + liveEntry.label, fmt(liveEntry.price), '#4a6741')}
          {investment.quantity      != null && row('Quantity / Units', Number(investment.quantity).toLocaleString('en-IN'))}
          {investment.purchase_price!= null && row('Purchase Price / Unit', fmt(investment.purchase_price))}
          {investment.interest_rate != null && row('Interest Rate', investment.interest_rate + '% p.a.')}
          {maturityVal != null       && row('Maturity Value (' + (investment.type === 'ppf' ? 'Yearly' : 'Quarterly') + ' CI)', fmt(maturityVal), '#4a6741')}
          {investment.purchase_date  && row('Purchase Date', fmtDate(investment.purchase_date))}
          {investment.maturity_date  && row('Maturity Date', fmtDate(investment.maturity_date))}
          {investment.sip_amount    != null && row('SIP Amount', fmt(investment.sip_amount) + (investment.sip_frequency ? ' / ' + investment.sip_frequency : ''))}
          {investment.scheme_code    && row('MF Scheme Code', investment.scheme_code)}
          {investment.ticker_symbol  && row('Ticker Symbol', investment.ticker_symbol)}
          {liveEntry?.error && (
            <div style={{ marginTop: '0.5rem', fontFamily: 'monospace', fontSize: '0.65rem', color: '#b8441a', background: 'rgba(184,68,26,0.06)', borderRadius: '4px', padding: '0.5rem 0.75rem' }}>
              ⚠ Live price error: {liveEntry.error}
            </div>
          )}
          {investment.notes && (
            <div style={{ marginTop: '0.75rem', background: 'var(--surface-hdr)', borderRadius: '6px', padding: '0.7rem 0.9rem' }}>
              <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>Notes</div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text)', lineHeight: 1.5 }}>{investment.notes}</div>
            </div>
          )}
        </div>

        {/* Record value */}
        <div style={{ borderTop: '1px solid var(--border)', padding: '1rem 1.5rem' }}>
          {!showForm ? (
            <button type="button" onClick={() => setShowForm(true)}
              style={{ background: 'var(--surface-hdr)', border: '1px solid var(--border)', borderRadius: '4px', padding: '0.5rem 1rem', fontFamily: 'monospace', fontSize: '0.72rem', cursor: 'pointer', color: 'var(--text-muted)', width: '100%', textAlign: 'center', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#c9a84c'; e.currentTarget.style.color = '#c9a84c' }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)' }}>
              📊 Record New Current Value
            </button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
              <div style={{ fontFamily: 'monospace', fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>Record Current Value</div>
              <input type="text" inputMode="decimal" placeholder="New current value (₹)"
                value={newVal} onChange={e => setNewVal(e.target.value)}
                style={inputStyle}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--text)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'} />
              <input type="text" placeholder="Notes (optional)"
                value={newNotes} onChange={e => setNewNotes(e.target.value)}
                style={inputStyle}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--text)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'} />
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowForm(false)}
                  style={{ background: 'transparent', border: '1px solid var(--border)', padding: '0.4rem 0.8rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.7rem', cursor: 'pointer', color: 'var(--text-muted)' }}>
                  Cancel
                </button>
                <button type="button" onClick={handleUpdate} disabled={updating}
                  style={{ background: '#4a6741', color: '#fff', border: 'none', padding: '0.4rem 0.9rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: '600', cursor: updating ? 'not-allowed' : 'pointer', opacity: updating ? 0.7 : 1 }}>
                  {updating ? 'Saving...' : 'Save Value'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Value history */}
        {invHistory.length > 0 && (
          <div style={{ borderTop: '1px solid var(--border)' }}>
            <div style={{ background: 'var(--surface-hdr)', padding: '0.7rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.9rem', fontWeight: '700' }}>Value History</span>
              <span style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'var(--text-muted)' }}>{invHistory.length} entries</span>
            </div>
            <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
              {invHistory.map((h, i) => {
                const prev = invHistory[i + 1]
                const chg = prev ? h.value - prev.value : null
                const cc  = chg == null ? 'var(--text-muted)' : chg >= 0 ? '#4a6741' : '#b8441a'
                return (
                  <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 1.5rem', borderBottom: i < invHistory.length - 1 ? '1px solid var(--surface-hdr)' : 'none' }}>
                    <div>
                      <div style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text)' }}>{fmtDate(h.recorded_at || h.created_at)}</div>
                      {h.notes && <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>{h.notes}</div>}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontFamily: 'Georgia,serif', fontSize: '0.9rem', fontWeight: '700', color: 'var(--text)' }}>{fmt(h.value)}</div>
                      {chg != null && <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: cc }}>{chg >= 0 ? '▲' : '▼'} {fmt(Math.abs(chg))}</div>}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Investments({ investments, investmentHistory, onSave, onDelete, onUpdateValue, onLivePriceUpdate, loading }) {
  const { fmt } = useCurrency()
  const [addModal,   setAddModal]   = useState(false)
  const [editInv,    setEditInv]    = useState(null)
  const [detailInv,  setDetailInv]  = useState(null)
  const [filterType, setFilterType] = useState('all')
  const [liveData,   setLiveData]   = useState({})   // { [invId]: { price, label, updatedAt, error } }
  const [refreshing, setRefreshing] = useState(false)

  // ── Derived totals ──
  const totalPortfolio = investments.reduce((s, i) => s + effectiveValue(i, liveData), 0)
  const totalInvested  = investments.reduce((s, i) => s + parseFloat(i.invested_amount || 0), 0)
  const totalPL        = totalPortfolio - totalInvested
  const totalPLPct     = totalInvested > 0 ? (totalPL / totalInvested) * 100 : 0
  const plClr          = totalPL >= 0 ? '#4a6741' : '#b8441a'

  const bestPerformer = investments.reduce((best, inv) => {
    const ii  = parseFloat(inv.invested_amount || 0)
    const cur = effectiveValue(inv, liveData)
    const pct = ii > 0 ? ((cur - ii) / ii) * 100 : -Infinity
    return pct > best.pct ? { pct, name: inv.name } : best
  }, { pct: -Infinity, name: null })

  const filtered = filterType === 'all'
    ? investments
    : investments.filter(i => i.type === filterType)

  // ── Live price refresh ──
  const refreshAllPrices = useCallback(async () => {
    if (investments.length === 0) return
    setRefreshing(true)
    const now = new Date()
    const next = {}
    const supabaseUpdates = [] // { id, value } — MF/Stocks/ETF only

    await Promise.allSettled(investments.map(async inv => {
      try {
        const qty = parseFloat(inv.quantity || 0)
        if (['mutual_fund', 'sip'].includes(inv.type) && inv.scheme_code) {
          const d = await fetchMFNav(inv.scheme_code)
          next[inv.id] = { ...d, updatedAt: now }
          if (qty > 0) supabaseUpdates.push({ id: inv.id, value: parseFloat((d.price * qty).toFixed(2)) })
        } else if (['stocks', 'etf'].includes(inv.type) && inv.ticker_symbol) {
          const d = await fetchStockPrice(inv.ticker_symbol)
          next[inv.id] = { ...d, updatedAt: now }
          if (qty > 0) supabaseUpdates.push({ id: inv.id, value: parseFloat((d.price * qty).toFixed(2)) })
        } else if (['fd', 'ppf'].includes(inv.type) && inv.invested_amount && inv.interest_rate && inv.purchase_date) {
          const n   = inv.type === 'ppf' ? 1 : 4
          const val = calcCompoundValue(inv.invested_amount, inv.interest_rate, inv.purchase_date, now.toISOString().slice(0, 10), n)
          if (val !== null) next[inv.id] = { price: val, label: 'Accrued', updatedAt: now }
          // FD/PPF: keep current_value manual — no Supabase update
        }
      } catch (e) {
        next[inv.id] = { error: e.message, updatedAt: now }
      }
    }))

    setLiveData(prev => ({ ...prev, ...next }))

    // Persist live-derived current_value to Supabase for MF / Stocks / ETF
    if (supabaseUpdates.length > 0 && onLivePriceUpdate) {
      await Promise.allSettled(supabaseUpdates.map(({ id, value }) => onLivePriceUpdate(id, value)))
    }

    setRefreshing(false)
  }, [investments, onLivePriceUpdate])

  // Auto-fetch live prices once when investments first become available
  const didAutoFetch = useRef(false)
  useEffect(() => {
    if (!didAutoFetch.current && investments.length > 0) {
      didAutoFetch.current = true
      refreshAllPrices()
    }
  }, [investments, refreshAllPrices])

  // ── Save handler ──
  async function handleSave(data) {
    await onSave(data)
    setAddModal(false)
    setEditInv(null)
  }

  async function handleUpdateValue(invId, value, notes) {
    await onUpdateValue(invId, value, notes)
    setDetailInv(prev => prev?.id === invId ? { ...prev, current_value: value } : prev)
  }

  if (loading) {
    return (
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '3rem', textAlign: 'center', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.8rem' }}>
        Loading investments...
      </div>
    )
  }

  const hasLiveData = Object.keys(liveData).length > 0
  const lastUpdated = hasLiveData
    ? Object.values(liveData).reduce((latest, d) => d.updatedAt > latest ? d.updatedAt : latest, new Date(0))
    : null

  return (
    <>
      {(addModal || editInv) && (
        <AddInvestmentModal
          investment={editInv}
          onSave={handleSave}
          onClose={() => { setAddModal(false); setEditInv(null) }}
        />
      )}
      {detailInv && (
        <InvestmentDetailModal
          investment={detailInv}
          history={investmentHistory}
          liveEntry={liveData[detailInv.id] || null}
          fmt={fmt}
          onUpdateValue={handleUpdateValue}
          onClose={() => setDetailInv(null)}
        />
      )}

      <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.4rem', fontWeight: '700', color: 'var(--text)' }}>Portfolio</div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
              {investments.length} investment{investments.length !== 1 ? 's' : ''}
              {lastUpdated && <span> · Prices updated {fmtTime(lastUpdated)}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {investments.length > 0 && (
              <button
                type="button"
                onClick={refreshAllPrices}
                disabled={refreshing}
                style={{ background: 'transparent', color: refreshing ? 'var(--text-muted)' : '#4a6741', border: '1px solid ' + (refreshing ? 'var(--border)' : '#4a6741'), padding: '0.5rem 0.9rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: '600', cursor: refreshing ? 'not-allowed' : 'pointer', transition: 'all 0.15s', opacity: refreshing ? 0.6 : 1 }}
              >
                {refreshing ? '⟳ Refreshing...' : '⟳ Refresh Prices'}
              </button>
            )}
            <button
              type="button"
              onClick={() => { setEditInv(null); setAddModal(true) }}
              style={{ background: 'var(--btn-bg)', color: 'var(--btn-text)', border: 'none', padding: '0.55rem 1.1rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: '500', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}
            >+ Add Investment</button>
          </div>
        </div>

        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
          {[
            { label: 'Portfolio Value', value: fmt(totalPortfolio), color: '#3d4a5c', sub: `${investments.length} holding${investments.length !== 1 ? 's' : ''}` },
            { label: 'Total Invested',  value: fmt(totalInvested),  color: '#6b4c9a', sub: 'cost basis' },
            {
              label: 'Total P&L',
              value: investments.length > 0 ? (totalPL >= 0 ? '+' : '−') + fmt(Math.abs(totalPL)) : '—',
              color: plClr,
              sub:   investments.length > 0 ? (totalPL >= 0 ? '+' : '−') + Math.abs(totalPLPct).toFixed(2) + '% return' : 'no data',
            },
            {
              label: 'Best Performer',
              value: bestPerformer.name ? (bestPerformer.pct >= 0 ? '+' : '') + bestPerformer.pct.toFixed(1) + '%' : '—',
              color: '#4a6741',
              sub:   bestPerformer.name || 'no investments',
            },
          ].map(c => (
            <div key={c.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.1rem 1.3rem', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: c.color }} />
              <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>{c.label}</div>
              <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.3rem', fontWeight: '700', color: c.color }}>{c.value}</div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'var(--text-muted)', marginTop: '0.2rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.sub}</div>
            </div>
          ))}
        </div>

        {/* Empty state */}
        {investments.length === 0 && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '3.5rem 2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📈</div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.1rem', fontWeight: '700', color: 'var(--text)', marginBottom: '0.4rem' }}>No investments yet</div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text-muted)', maxWidth: '380px', margin: '0 auto 1.25rem', lineHeight: 1.6 }}>
              Add your FDs, PPF, mutual funds, stocks, and other investments to track your portfolio with live prices.
            </div>
            <button type="button" onClick={() => setAddModal(true)}
              style={{ background: 'var(--btn-bg)', color: 'var(--btn-text)', border: 'none', padding: '0.6rem 1.2rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: '500', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              + Add First Investment
            </button>
          </div>
        )}

        {investments.length > 0 && (
          <>
            {/* Type filter */}
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              {[{ value: 'all', label: 'All', icon: '📁', color: '#3d4a5c' }, ...INVESTMENT_TYPES].map(t => {
                const count = t.value === 'all' ? investments.length : investments.filter(i => i.type === t.value).length
                if (t.value !== 'all' && count === 0) return null
                const active = filterType === t.value
                return (
                  <button key={t.value} type="button" onClick={() => setFilterType(t.value)}
                    style={{ background: active ? t.color : 'var(--surface)', color: active ? '#fff' : 'var(--text-muted)', border: `1px solid ${active ? t.color : 'var(--border)'}`, padding: '0.28rem 0.75rem', borderRadius: '99px', fontFamily: 'monospace', fontSize: '0.65rem', cursor: 'pointer', transition: 'all 0.15s' }}>
                    {t.icon} {t.label} ({count})
                  </button>
                )
              })}
            </div>

            {/* Investment cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {filtered.map(inv => {
                const t       = getType(inv.type)
                const invested = parseFloat(inv.invested_amount || 0)
                const qty      = parseFloat(inv.quantity || 0)
                const live     = liveData[inv.id]
                const current  = effectiveValue(inv, liveData)
                const costBasis = (inv.purchase_price != null && qty > 0)
                  ? parseFloat(inv.purchase_price) * qty
                  : invested
                const pl       = current - costBasis
                const plPct    = costBasis > 0 ? (pl / costBasis) * 100 : 0
                const plClr    = pl > 0 ? '#4a6741' : pl < 0 ? '#b8441a' : 'var(--text-muted)'
                const plSign   = pl >= 0 ? '+' : '−'

                return (
                  <div key={inv.id}
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', transition: 'box-shadow 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                  >
                    <div style={{ display: 'flex' }}>
                      <div style={{ width: '3px', background: t.color, flexShrink: 0 }} />
                      <div style={{ flex: 1, padding: '0.9rem 1.1rem' }}>

                        {/* Name + P&L */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.55rem' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.2rem' }}>
                              <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.95rem', fontWeight: '700', color: 'var(--text)' }}>{inv.name}</span>
                              <span style={{ fontFamily: 'monospace', fontSize: '0.58rem', padding: '0.1rem 0.4rem', borderRadius: '3px', background: t.color + '18', color: t.color, border: '1px solid ' + t.color + '40', textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>
                                {t.icon} {t.label}
                              </span>
                              {live && !live.error && (
                                <span style={{ fontFamily: 'monospace', fontSize: '0.56rem', padding: '0.08rem 0.35rem', borderRadius: '3px', background: 'rgba(74,103,65,0.1)', color: '#4a6741', border: '1px solid rgba(74,103,65,0.25)', flexShrink: 0 }}>
                                  ● Live {fmtTime(live.updatedAt)}
                                </span>
                              )}
                              {live?.error && (
                                <span style={{ fontFamily: 'monospace', fontSize: '0.56rem', padding: '0.08rem 0.35rem', borderRadius: '3px', background: 'rgba(184,68,26,0.08)', color: '#b8441a', border: '1px solid rgba(184,68,26,0.25)', flexShrink: 0 }}>
                                  ⚠ Fetch failed
                                </span>
                              )}
                              {inv.maturity_date && new Date(inv.maturity_date) < new Date(Date.now() + 30*24*60*60*1000) && new Date(inv.maturity_date) > new Date() && (
                                <span style={{ fontFamily: 'monospace', fontSize: '0.56rem', padding: '0.08rem 0.35rem', borderRadius: '3px', background: 'rgba(201,168,76,0.12)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.3)', flexShrink: 0 }}>
                                  ⏰ Matures soon
                                </span>
                              )}
                            </div>
                            {inv.purchase_date && (
                              <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'var(--text-muted)' }}>
                                Since {fmtDate(inv.purchase_date)}
                                {inv.ticker_symbol  && <span> · {inv.ticker_symbol}</span>}
                                {inv.scheme_code    && <span> · #{inv.scheme_code}</span>}
                              </div>
                            )}
                          </div>
                          {/* P&L badge */}
                          <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '1rem', background: pl >= 0 ? 'rgba(74,103,65,0.08)' : 'rgba(184,68,26,0.08)', border: `1px solid ${plClr}30`, borderRadius: '6px', padding: '0.3rem 0.65rem' }}>
                            <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: plClr, marginBottom: '0.1rem' }}>
                              {plSign}{Math.abs(plPct).toFixed(2)}%
                            </div>
                            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1rem', fontWeight: '700', color: plClr }}>
                              {plSign}{fmt(Math.abs(pl))}
                            </div>
                          </div>
                        </div>

                        {/* Values */}
                        <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '0.65rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                          {[
                            { label: 'Invested',      value: fmt(invested),  always: true },
                            { label: 'Current Value', value: fmt(current),   always: true, vc: plClr },
                            live && !live.error && inv.quantity
                              ? { label: 'Live ' + live.label, value: fmt(live.price) + '/unit', vc: '#4a6741' }
                              : null,
                            inv.quantity      ? { label: ['mutual_fund','sip'].includes(inv.type) ? 'Units' : 'Qty', value: Number(inv.quantity).toLocaleString('en-IN') } : null,
                            inv.interest_rate ? { label: 'Rate', value: inv.interest_rate + '%' } : null,
                            inv.sip_amount    ? { label: 'SIP',  value: fmt(inv.sip_amount) + '/' + (inv.sip_frequency || 'mo') } : null,
                            inv.maturity_date ? { label: 'Matures', value: fmtDate(inv.maturity_date) } : null,
                          ].filter(Boolean).filter(f => f.always || f.value).map((f, idx) => (
                            <React.Fragment key={f.label}>
                              {idx > 0 && <div style={{ width: '1px', background: 'var(--border)', flexShrink: 0, alignSelf: 'stretch', minHeight: '28px' }} />}
                              <div>
                                <div style={{ fontFamily: 'monospace', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.15rem' }}>{f.label}</div>
                                <div style={{ fontFamily: 'Georgia,serif', fontSize: '0.88rem', fontWeight: '700', color: f.vc || 'var(--text)' }}>{f.value}</div>
                              </div>
                            </React.Fragment>
                          ))}
                        </div>

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: '0.4rem' }}>
                          <button type="button"
                            onClick={() => setDetailInv(inv)}
                            style={{ background: 'none', border: '1px solid var(--border)', padding: '0.22rem 0.6rem', borderRadius: '3px', fontSize: '0.68rem', fontFamily: 'monospace', cursor: 'pointer', color: 'var(--text-muted)', transition: 'all 0.15s' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = '#3d4a5c'; e.currentTarget.style.color = '#3d4a5c' }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)' }}>
                            Detail
                          </button>
                          <button type="button"
                            onClick={() => setEditInv(inv)}
                            style={{ background: 'none', border: '1px solid var(--border)', padding: '0.22rem 0.6rem', borderRadius: '3px', fontSize: '0.68rem', fontFamily: 'monospace', cursor: 'pointer', color: 'var(--text-muted)', transition: 'all 0.15s' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--text)'; e.currentTarget.style.color = 'var(--text)' }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)' }}>
                            Edit
                          </button>
                          <button type="button"
                            onClick={() => onDelete(inv.id)}
                            style={{ background: 'none', border: '1px solid var(--border)', padding: '0.22rem 0.6rem', borderRadius: '3px', fontSize: '0.68rem', fontFamily: 'monospace', cursor: 'pointer', color: 'var(--text-muted)', transition: 'all 0.15s' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = '#c0392b'; e.currentTarget.style.color = '#c0392b' }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)' }}>
                            Remove
                          </button>
                        </div>

                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Allocation breakdown */}
            {investments.length >= 2 && (
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
                <div style={{ background: 'var(--surface-hdr)', borderBottom: '1px solid var(--border)', padding: '1rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Allocation</span>
                  <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.9rem', fontWeight: '700', color: '#3d4a5c' }}>{fmt(totalPortfolio)}</span>
                </div>
                <div style={{ padding: '1.25rem 1.5rem' }}>
                  <div style={{ display: 'flex', height: '10px', borderRadius: '99px', overflow: 'hidden', marginBottom: '1.25rem', background: 'var(--surface-hdr)' }}>
                    {INVESTMENT_TYPES.map(t => {
                      const tt = investments.filter(i => i.type === t.value).reduce((s, i) => s + effectiveValue(i, liveData), 0)
                      const w  = totalPortfolio > 0 ? (tt / totalPortfolio) * 100 : 0
                      if (w < 0.5) return null
                      return <div key={t.value} style={{ height: '100%', width: w + '%', background: t.color }} title={`${t.label}: ${w.toFixed(1)}%`} />
                    })}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                    {INVESTMENT_TYPES.map(t => {
                      const typeInvs = investments.filter(i => i.type === t.value)
                      if (typeInvs.length === 0) return null
                      const tt   = typeInvs.reduce((s, i) => s + effectiveValue(i, liveData), 0)
                      const tpl  = typeInvs.reduce((s, i) => s + (effectiveValue(i, liveData) - parseFloat(i.invested_amount || 0)), 0)
                      const share = totalPortfolio > 0 ? (tt / totalPortfolio) * 100 : 0
                      return (
                        <div key={t.value}
                          onClick={() => setFilterType(filterType === t.value ? 'all' : t.value)}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', padding: '0.35rem 0.65rem', borderRadius: '6px', background: filterType === t.value ? t.color + '14' : 'transparent', border: `1px solid ${filterType === t.value ? t.color + '40' : 'transparent'}`, transition: 'all 0.15s' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: t.color, flexShrink: 0 }} />
                          <span style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)' }}>{t.icon} {t.label}</span>
                          <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.78rem', fontWeight: '700', color: 'var(--text)' }}>{fmt(tt)}</span>
                          <span style={{ fontFamily: 'monospace', fontSize: '0.58rem', color: 'var(--text-muted)' }}>({share.toFixed(1)}%)</span>
                          <span style={{ fontFamily: 'monospace', fontSize: '0.58rem', color: tpl >= 0 ? '#4a6741' : '#b8441a' }}>
                            {tpl >= 0 ? '+' : '−'}{fmt(Math.abs(tpl))}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

      </div>
    </>
  )
}
